import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import { ApplicantStatusesEnum } from '@thinknet-gmbh/platform-common';
import {
  Button,
  Typography,
  TYPOGRAPHY_SIZE,
  PLATFORM_THEME_COLORS,
  ICONS,
  Loader,
  ButtonsWrapper,
} from '@thinknet-gmbh/platform-common-ui';

import { useGetApplicantById } from 'src/hooks/applicants/get-applicant-by-id.hook';
import { useUpdateApplicantById } from 'src/hooks/applicants/update-applicant-by-id.hook';
import { useRouteQuery } from 'src/common/helpers';

import { ApplicantDetailsInfo } from './components/applicant-details-info';
import { CandidateDetailsWrapper, HeaderContainer } from './applicant-details.styles';
import { ApplicantDetailsForm } from './components/applicant-details-form';
import { ApplicantFeedbacks } from './components/applicant-feedbacks';
import { EmailTemplateSelectModal } from './components/email-template-select-modal';
import { APPLICANT_SEND_MODALS } from './applicant-details.constants';
import { TakeApplicantAsEmployeeSelectModal } from './components/take-applicant-as-employee-select-modal';
import { CreateEmployeeByQuestionnaireModal } from './components/create-employee-by-questionnaire-modal';

const CandidateDetails = ({
  responsiblePersons,
}: {
  responsiblePersons: { _id: string; profile: { displayName: string } };
}) => {
  const [activeModal, setActiveModal] = useState<null | string>(null);
  const { t } = useTranslation(['employee', 'vacancies']);
  const [isAddedAdditionalInterview, setIsAddedAdditionalInterview] = useState(false);
  const query = useRouteQuery();
  const id = query.get('applicantId') || '';

  const { applicant, loading } = useGetApplicantById(id);
  const { updateApplicant } = useUpdateApplicantById({});

  const isRefused = applicant.status === ApplicantStatusesEnum.REFUSED;
  const isHired = applicant.status === ApplicantStatusesEnum.HIRED;
  const isOfferAccepted = applicant.status === ApplicantStatusesEnum.OFFER_ACCEPTED;
  const isOfferSent = applicant.status === ApplicantStatusesEnum.OFFER_SENT;
  const isQuestionnaireSent = applicant.status === ApplicantStatusesEnum.QUESTIONNAIRE_SENT;

  const closeModal = () => setActiveModal(null);

  const handleOpenQuestionnaireModal = () =>
    setActiveModal(APPLICANT_SEND_MODALS.createEmployeeByQuestionnaire);

  if (loading) {
    return <Loader />;
  }

  return (
    <CandidateDetailsWrapper>
      <EmailTemplateSelectModal
        isOpened={activeModal === APPLICANT_SEND_MODALS.sendAnOffer}
        onClose={closeModal}
        applicantId={id}
        title={t('vacancies:buttons.sendAnOffer')}
        status={ApplicantStatusesEnum.OFFER_SENT}
      />
      <TakeApplicantAsEmployeeSelectModal
        onClose={closeModal}
        applicantId={applicant?._id}
        openQuestionnaireModal={handleOpenQuestionnaireModal}
        isOpened={activeModal === APPLICANT_SEND_MODALS.takeApplicantAsEmployee}
      />
      <CreateEmployeeByQuestionnaireModal
        isOpened={activeModal === APPLICANT_SEND_MODALS.createEmployeeByQuestionnaire}
        onClose={closeModal}
        applicant={applicant}
      />
      <EmailTemplateSelectModal
        isOpened={activeModal === APPLICANT_SEND_MODALS.refuse}
        onClose={closeModal}
        applicantId={id}
        title={t('vacancies:buttons.refuse')}
        status={ApplicantStatusesEnum.REFUSED}
      />
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('vacancies:labels.candidateDetails')}
        </Typography>
        <ButtonsWrapper>
          {!isHired && !isQuestionnaireSent && (
            <>
              {!isOfferSent && (
                <>
                  {!isRefused && !isOfferAccepted && (
                    <Button
                      className="refuse"
                      type="primary"
                      ghost
                      icon={ICONS.xCircleRegular}
                      iconColor={PLATFORM_THEME_COLORS.tone.red}
                      onClick={() => setActiveModal(APPLICANT_SEND_MODALS.refuse)}
                    >
                      {t('vacancies:buttons.refuse')}
                    </Button>
                  )}
                  <Button
                    type="primary"
                    ghost
                    icon={isOfferAccepted ? ICONS.userPlus : ICONS.mailSend}
                    iconColor={PLATFORM_THEME_COLORS.primary.blue}
                    onClick={() => {
                      if (isOfferAccepted) {
                        return setActiveModal(APPLICANT_SEND_MODALS.takeApplicantAsEmployee);
                      }
                      setActiveModal(APPLICANT_SEND_MODALS.sendAnOffer);
                    }}
                  >
                    {isOfferAccepted
                      ? t('vacancies:buttons.takeApplicantAsEmployee')
                      : t('vacancies:buttons.sendAnOffer')}
                  </Button>
                </>
              )}
              {isOfferSent && (
                <Button
                  type="primary"
                  icon={ICONS.check}
                  iconColor={PLATFORM_THEME_COLORS.primary.blue}
                  onClick={() =>
                    updateApplicant({
                      variables: {
                        applicantId: id,
                        payload: { status: ApplicantStatusesEnum.OFFER_ACCEPTED },
                      },
                    })
                  }
                  ghost
                >
                  {t('vacancies:buttons.acceptOffer')}
                </Button>
              )}
              {!isRefused && (
                <Button
                  type="primary"
                  icon={ICONS.plus}
                  iconColor={PLATFORM_THEME_COLORS.primary.white}
                  onClick={() => setIsAddedAdditionalInterview(true)}
                >
                  {t('employee:employeeInterview.buttons.addNewInterview')}
                </Button>
              )}
            </>
          )}
        </ButtonsWrapper>
      </HeaderContainer>
      <ApplicantDetailsInfo applicant={applicant} />
      {applicant.status !== ApplicantStatusesEnum.NEW && applicant.feedbacks.length > 0 && (
        <ApplicantFeedbacks applicant={applicant} responsiblePersons={responsiblePersons} />
      )}

      <ApplicantDetailsForm
        isAddedAdditionalInterview={isAddedAdditionalInterview}
        setIsAddedAdditionalInterview={setIsAddedAdditionalInterview}
        applicantId={id}
        initialValues={{ ...applicant, responsiblePersons }}
      />
    </CandidateDetailsWrapper>
  );
};

export default CandidateDetails;
